/**
 * Lookup de contacto por teléfono contra el **main API** (`GET /contacts/search`), para enriquecer
 * las conversaciones entrantes (que sólo traen el pushName/JID de WhatsApp).
 *
 * El match de whatsapp en el backend es `LIKE 'q%'` (prefijo), y el formato guardado puede variar
 * (+54…, 549…, etc.) → best-effort: probamos algunas variantes de los dígitos del JID.
 */
import { axiosInstance } from "@/utilities";
import { API_BASE_URL } from "@/services/XCONS_ENV";

export interface ContactMatch {
  /** `null` cuando la persona no tiene ficha en el store (ver `needsContactInStore`). */
  contactId: number | null;
  userId?: number;
  name: string;
  email?: string;
  whatsapp?: string;
  company?: string;
  /** Store al que pertenece el contacto. */
  storeCode?: string;
  /** Marcadores de operador que trae el search: {store} = op. store, {seller} = op. seller. */
  permissions?: Array<{ store?: string; seller?: string }>;
  /**
   * La persona es operadora del store (o de un seller asociado) pero **no tiene ficha acá**: el
   * search la devuelve con `contactId: null`. La ficha se crea al elegirla.
   */
  needsContactInStore?: boolean;
}

export type ContactLevelKind = "customer" | "store" | "seller";

/**
 * #111: nivel del contacto para el picker. Sin permisos de operador ⇒ Customer del store;
 * con {store} ⇒ Operador del Store; con {seller} ⇒ Operador del Seller.
 */
export function contactLevel(m: ContactMatch): { kind: ContactLevelKind; label: string; name: string } {
  const perms = m.permissions ?? [];
  const seller = perms.find((p) => p.seller)?.seller;
  const store = perms.find((p) => p.store)?.store;
  if (seller) return { kind: "seller", label: "Op. Seller", name: seller };
  if (store) return { kind: "store", label: "Op. Store", name: store };
  return { kind: "customer", label: "Customer", name: m.storeCode ?? "" };
}



// Los teléfonos viven en un módulo puro, para poder usarlos sin el entorno de Vite. Se re-exportan
// acá para que los que ya los importaban de este archivo no cambien.
export { formatWhatsappPhone, phoneFromThread, toWhatsappJid } from "./whatsapp-phone";
import { phoneFromThread } from "./whatsapp-phone";
import { identifierFor } from "./channel-types";

/**
 * Identificador a mostrar cuando NO hay contacto registrado, según el tipo de canal.
 * V1: whatsapp → número formateado. Otros tipos (email/messenger) a futuro muestran su identifier.
 */
export function identifierDisplay(channelType: string, externalThreadId: string): string {
  // Los dos WhatsApp —QR y Cloud API— muestran el teléfono formateado; un BSUID, no (D20).
  return identifierFor(channelType, externalThreadId);
}

/**
 * Variantes para el match starts-with del backend, de más a menos específica.
 * - Clave AR: WhatsApp da `54 9 <area><num>` (móvil con 9), pero en `contact` se guarda **sin el 9**
 *   (`54 <area><num>`). Ej: 5493517330911 → 543517330911.
 * - Data sucia: muchos whatsapp están guardados con **comillas literales** (`"543517330911"`),
 *   así que por cada forma probamos cruda, con `+` y con comilla inicial (`"`), que matchea el prefijo.
 */
/**
 * Query único para buscar un contacto por teléfono.
 *
 * Antes se probaban hasta 12 variantes en requests secuenciales (con/sin `54`, con/sin el `9` del
 * móvil AR, con `+`, y con la comilla que trae la data sucia de `user.whatsapp`). Ya no hace falta:
 * el backend matchea `cu.whatsapp LIKE %q%`, así que los **últimos 10 dígitos** —el número nacional
 * significativo, área + local— son substring de todas esas formas:
 *
 *   3512042412  ⊂  5493512042412  ⊂  543512042412  ⊂  +54 351 204-2412  ⊂  "5493512042412"
 *
 * Si el número tiene menos de 10 dígitos se usa completo.
 */
export function phoneQuery(digits: string): string {
  return digits.length > 10 ? digits.slice(-10) : digits;
}

/**
 * De los resultados del substring, elige el que realmente ES este teléfono.
 *
 * El `LIKE %q%` puede matchear en el medio de otro número, así que no alcanza con tomar la primera
 * fila: se exige que los dígitos del candidato terminen igual (o que el candidato sea un sufijo del
 * buscado, para los guardados sin código de área) y se prefiere el match más largo.
 */
export function pickPhoneMatch<T>(rows: T[], digits: string, whatsappOf: (row: T) => string | undefined): T | null {
  const q = phoneQuery(digits);
  let best: T | null = null;
  let bestLen = -1;
  for (const row of rows) {
    const cand = (whatsappOf(row) ?? "").replace(/\D/g, "");
    if (!cand) continue;
    if (!cand.endsWith(q) && !q.endsWith(cand)) continue;
    if (cand.length > bestLen) {
      best = row;
      bestLen = cand.length;
    }
  }
  return best;
}

/**
 * #112: variantes de búsqueda para un número tipeado sin código de país. El backend matchea por
 * prefijo, así que prependeamos 54/549 (y, si vino con 9, la forma sin 9 como en `contact`).
 */


export interface ContactParticipation {
  name: string;
  type: string; // 'store' | 'seller' | ''
  identifier: string;
  role?: string;
  userId?: number;
  /**
   * Orden dentro de su nivel. El **dueño** de cada nivel es el de `position` más baja — es la
   * convención canónica del admin y de la API (ver `pages/quotes/utils/resolveOwner.ts`).
   */
  position?: number;
  /** Contacto del operador que participa, para linkear a su ficha. */
  contactId?: number;
}
export interface ContactGroup {
  name: string;
  color?: string;
}
export interface ContactDetail {
  createdAt?: string;
  company?: string;
  firstname?: string;
  lastname?: string;
  userId?: number;
  additionalData: Record<string, string>;
  participations: ContactParticipation[];
  groups: ContactGroup[];
}

export interface OperatorRef {
  contactId: number;
  /** Nombre completo (`firstname lastname`), no el que quedó guardado en el mensaje. */
  name: string;
}

/**
 * Resuelve el **operador** que envió un mensaje: su ficha de contacto en ese store y su nombre real.
 *
 * Va por `GET /contacts/directory` filtrando por `userId` + `storeCode`, que es una consulta barata:
 * un solo join a `user`, filtro por igualdad y con índice compuesto `(store_code, user_id)`. Nada que
 * ver con `/contacts/search`, que matchea el teléfono con `LIKE '%…%'` sin índice.
 *
 * **El store importa**: un mismo usuario tiene un contacto por store (el 5 es `contactId` 5 en
 * `puntoseco` y 626 en `vv`), así que sin filtrar por store el link lleva a la ficha equivocada.
 */
export async function findOperatorContact(
  userId: number,
  storeCode: string,
): Promise<OperatorRef | null> {
  if (!userId || !storeCode) return null;
  try {
    const { data } = await axiosInstance.get(`${API_BASE_URL}/contacts/directory`, {
      params: {
        "filter.userId": `$eq:${userId}`,
        "filter.storeCode": `$eq:${storeCode}`,
        limit: 1,
      },
    });
    const row: any = (Array.isArray(data) ? data : data?.data ?? [])[0];
    if (!row) return null;
    const u = row.user ?? {};
    const name = [u.firstname, u.lastname].filter(Boolean).join(" ").trim();
    const contactId = row.contactId ?? row.contact_id;
    if (!contactId) return null;
    return { contactId, name };
  } catch {
    return null;
  }
}

export const contactsApi = {
  /** Detalle enriquecido del contacto (additionalData + relations + groups) reusando el main API. */
  async getContactDetail(contactId: number): Promise<ContactDetail> {
    const [rec, grp] = await Promise.allSettled([
      axiosInstance.get(`${API_BASE_URL}/contacts`, { params: { "filter.contactId": contactId } }),
      axiosInstance.get(`${API_BASE_URL}/contact-groups/relations`, {
        params: { "filter.contactId": contactId },
      }),
    ]);
    const contact = rec.status === "fulfilled" ? (rec.value.data?.data ?? [])[0] : undefined;
    const groupRows = grp.status === "fulfilled" ? grp.value.data?.data ?? grp.value.data ?? [] : [];
    const participations: ContactParticipation[] = (contact?.participations ?? []).map((p: any) => ({
      name: [p?.contact?.user?.firstname, p?.contact?.user?.lastname].filter(Boolean).join(" ") || "—",
      type: (p?.participationType ?? "").trim(),
      identifier: (p?.participationIdentifier ?? "").trim(),
      role: p?.value,
      userId: p?.contact?.user?.userId ?? p?.contact?.userId,
      position: Number(p?.position) || 0,
      contactId: p?.contactId ?? p?.contact?.contactId,
    }));
    const groups: ContactGroup[] = (Array.isArray(groupRows) ? groupRows : []).map((r: any) => ({
      name: r?.contactGroup?.name ?? r?.group?.name ?? r?.name ?? "grupo",
      color: r?.contactGroup?.color ?? r?.group?.color ?? r?.color,
    }));
    return {
      createdAt: contact?.createdAt,
      company: contact?.user?.company ?? contact?.company,
      firstname: contact?.user?.firstname,
      lastname: contact?.user?.lastname,
      userId: contact?.user?.userId ?? contact?.userId,
      additionalData: contact?.additionalData ?? {},
      participations,
      groups,
    };
  },

  /**
   * Búsqueda de contactos del store (nombre / email / whatsapp) para el modal de Nuevo mensaje.
   * #112: el backend matchea whatsapp por prefijo (`LIKE 'q%'`) y el número se guarda con código de
   * país (54, sin el 9). Si la query es numérica, probamos variantes **prependeando** 54/549 (y sin
   * el 9) para que un número local encuentre al contacto sin tener que tipear el prefijo.
   */
  async searchContacts(q: string, storeCode: string): Promise<ContactMatch[]> {
    if (!q.trim()) return [];
    const unquote = (s?: string) => (typeof s === "string" ? s.replace(/^"|"$/g, "") : s);
    const digits = q.replace(/\D/g, "");
    // Query "de teléfono" = solo dígitos/símbolos telefónicos y al menos 4 dígitos.
    const isPhone = digits.length >= 4 && /^[\d\s()+-]+$/.test(q.trim());
    // Teléfono → un solo query por substring (ver `phoneQuery`). Texto → tal cual lo escribió el operador.
    const queries = isPhone ? [phoneQuery(digits)] : [q];

    /**
     * La clave de deduplicación es el **userId**, no el `contactId`.
     *
     * `GET /contacts/search` devuelve, además de las fichas del store, a los **operadores sin
     * ficha** en él (propios o de un seller asociado) con `contactId: null` — son los mismos que
     * el buscador de operadores ofrece y a los que se les crea la ficha al elegirlos. Con un
     * `Set` de `contactId`, el primer `null` se guardaba y **todos los demás se descartaban**:
     * de los 85 operadores sin ficha que tiene `vv`, el picker mostraba uno solo.
     *
     * El `userId` identifica a la persona en las dos formas de fila, así que dedupea las dos.
     */
    const seen = new Set<number>();
    const out: ContactMatch[] = [];
    try {
      for (const query of queries) {
        const { data } = await axiosInstance.get(`${API_BASE_URL}/contacts/search`, {
          params: { q: query, storeCode, limit: 10 },
        });
        const rows: any[] = Array.isArray(data) ? data : data?.data ?? [];
        for (const r of rows) {
          if (!r?.user) continue;
          const userId = r.user.userId ?? r.userId;
          const key = Number(userId);
          if (!Number.isFinite(key) || seen.has(key)) continue;
          seen.add(key);
          out.push({
            contactId: r.contactId,
            userId,
            name: [r.user.firstname, r.user.lastname].filter(Boolean).join(" ") || unquote(r.user.whatsapp) || q,
            email: r.user.email,
            whatsapp: unquote(r.user.whatsapp),
            company: r.user.company ?? r.company,
            storeCode: r.storeCode,
            permissions: Array.isArray(r.permissions) ? r.permissions : [],
            needsContactInStore: !r.contactId,
          });
        }
        if (out.length >= 10) break;
      }
      return out.slice(0, 10);
    } catch {
      return out;
    }
  },

  /**
   * Crea la ficha del store para una persona que todavía no la tiene, y devuelve su `contactId`.
   *
   * Es el mismo paso que hace el buscador de operadores cuando se elige a alguien sin ficha: la
   * persona ya existe como identidad (por eso viaja el `userId`), lo que falta es su ficha en
   * **este** store. `POST /contacts` resuelve el get-or-create por `(storeCode, userId)`, así que
   * repetirlo es inofensivo.
   */
  async ensureContactInStore(userId: number, storeCode: string): Promise<number | null> {
    if (!userId || !storeCode) return null;
    try {
      const { data } = await axiosInstance.post(`${API_BASE_URL}/contacts`, {
        storeCode,
        userId,
        description: "Loggued in store",
        isActive: true,
        additionalData: {},
      });
      const row = data?.contact ?? data;
      const contactId = row?.contactId ?? row?.contact_id;
      return Number.isFinite(Number(contactId)) ? Number(contactId) : null;
    } catch {
      return null;
    }
  },

  /** #117: grupos de contacto del store (para el dropdown del filtro). */
  async listContactGroups(storeCode: string): Promise<{ id: number; name: string }[]> {
    try {
      const { data } = await axiosInstance.get(`${API_BASE_URL}/contact-groups`, {
        params: { "filter.storeCode": `$eq:${storeCode}`, limit: 100 },
      });
      const rows: any[] = data?.data ?? [];
      return rows.map((g) => ({ id: g.contactGroupId ?? g.id, name: g.name }));
    } catch {
      return [];
    }
  },

  /** #117: contactIds miembros de un grupo (para pasarlos al list de conversaciones). */
  async groupContactIds(contactGroupId: number): Promise<number[]> {
    try {
      const { data } = await axiosInstance.get(`${API_BASE_URL}/contact-groups/relations`, {
        params: { "filter.contactGroupId": `$eq:${contactGroupId}`, limit: 500 },
      });
      const rows: any[] = data?.data ?? [];
      return rows.map((r) => r.contactId).filter((n: any) => Number.isFinite(n));
    } catch {
      return [];
    }
  },

  async findByPhone(externalThreadId: string, storeCode: string): Promise<ContactMatch | null> {
    const digits = phoneFromThread(externalThreadId);
    if (!digits) return null;
    const unquote = (x?: string) => (typeof x === "string" ? x.replace(/^"|"$/g, "") : x);
    try {
      // UNA sola request: el backend matchea por substring, así que los últimos 10 dígitos cubren
      // todas las formas en que puede estar guardado el número.
      const { data } = await axiosInstance.get(`${API_BASE_URL}/contacts/search`, {
        params: { q: phoneQuery(digits), storeCode, limit: 5 },
      });
      const rows: any[] = (Array.isArray(data) ? data : data?.data ?? []).filter((r: any) => r?.user);
      const hit = pickPhoneMatch(rows, digits, (r: any) => r.user?.whatsapp);
      if (!hit) return null;
      const u = (hit as any).user;
      return {
        contactId: (hit as any).contactId,
        userId: u.userId ?? (hit as any).userId,
        name: [u.firstname, u.lastname].filter(Boolean).join(" ") || unquote(u.whatsapp) || digits,
        email: u.email,
        whatsapp: unquote(u.whatsapp),
        company: u.company ?? (hit as any).company,
      };
    } catch {
      return null;
    }
  },
};
